import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { RootState } from 'app/store';
import { getData } from 'features/dinamic';
import { setDataToast, setVisibleToast } from 'features/toast';
import AlertInterface from 'features/toast/toastInterface';
import saveAs from 'file-saver';
import { buildDiplomaPdfBlob } from 'helpers/diplomaPdf';

type LoadStatus = 'idle' | 'loading' | 'completed' | 'failed';

interface AlumnoDiplomaState {
  diplomas: any[];
  status: LoadStatus;
  error: string | null;
  downloadStatus: LoadStatus;
  downloadingId: number | null;
}

const initialState: AlumnoDiplomaState = {
  diplomas: [],
  status: 'idle',
  error: null,
  downloadStatus: 'idle',
  downloadingId: null,
};

const parseData = (data: any) => {
  if (!data) return null;
  if (typeof data === 'string') {
    try {
      return JSON.parse(data);
    } catch (_) {
      return data;
    }
  }
  return data;
};

export const fetchAlumnoDiplomas = createAsyncThunk(
  'alumnoDiplomas/fetchListado',
  async (_: void, { dispatch, getState, rejectWithValue }) => {
    const { result, message, data } = await getData({ idsp: 237 }, getState, dispatch);
    if (result) {
      const parsed = parseData(data);
      return Array.isArray(parsed) ? parsed : [];
    }
    return rejectWithValue(message || 'No fue posible obtener los diplomas');
  }
);

export const downloadAlumnoDiplomaPdf = createAsyncThunk(
  'alumnoDiplomas/downloadPdf',
  async (
    { idDiplomaAutorizacion }: { idDiplomaAutorizacion: number },
    { dispatch, getState, rejectWithValue }
  ) => {
    const { result, message, data } = await getData(
      { idsp: 238, idDiplomaAutorizacion },
      getState,
      dispatch
    );

    if (!result) {
      const alertFail: AlertInterface = {
        title: 'ACCIÓN INCOMPLETA',
        message: message || 'No fue posible obtener el diploma',
        data,
        type: 'Crimson',
        time: Date.now()
      };
      dispatch(setDataToast(alertFail));
      dispatch(setVisibleToast(true));
      setTimeout(() => {
        dispatch(setVisibleToast(false));
      }, 4000);
      return rejectWithValue(message || 'No fue posible obtener el diploma');
    }

    const payload = parseData(data);
    if (!payload) {
      return rejectWithValue('Respuesta inválida');
    }

    const pdfBlob = await buildDiplomaPdfBlob(payload);
    const fileName = `Diploma_${payload?.carnet || payload?.idDiplomaAutorizacion}.pdf`;
    saveAs(pdfBlob, fileName);

    const alertOk: AlertInterface = {
      title: 'CORRECTO..!',
      message: 'DIPLOMA DESCARGADO CORRECTAMENTE',
      data: [],
      type: 'LimeGreen',
      time: Date.now()
    };
    dispatch(setDataToast(alertOk));
    dispatch(setVisibleToast(true));
    setTimeout(() => {
      dispatch(setVisibleToast(false));
    }, 4000);

    return payload;
  }
);

const alumnoDiplomaSlice = createSlice({
  name: 'alumnoDiplomas',
  initialState,
  reducers: {
    resetAlumnoDiplomas: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAlumnoDiplomas.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(fetchAlumnoDiplomas.fulfilled, (state, action) => {
        state.status = 'completed';
        state.diplomas = action.payload || [];
      })
      .addCase(fetchAlumnoDiplomas.rejected, (state, action) => {
        state.status = 'failed';
        state.error = (action.payload as string) || 'Error al cargar diplomas';
      })
      .addCase(downloadAlumnoDiplomaPdf.pending, (state, action) => {
        state.downloadStatus = 'loading';
        state.downloadingId = action.meta.arg.idDiplomaAutorizacion;
      })
      .addCase(downloadAlumnoDiplomaPdf.fulfilled, (state) => {
        state.downloadStatus = 'completed';
        state.downloadingId = null;
      })
      .addCase(downloadAlumnoDiplomaPdf.rejected, (state, action) => {
        state.downloadStatus = 'failed';
        state.downloadingId = null;
        state.error = (action.payload as string) || 'Error al descargar diploma';
      });
  },
});

export const { resetAlumnoDiplomas } = alumnoDiplomaSlice.actions;

export const selectAlumnoDiplomas = (state: RootState) => state.alumnoDiplomas.diplomas;
export const selectAlumnoDiplomasStatus = (state: RootState) => state.alumnoDiplomas.status;
export const selectAlumnoDiplomasError = (state: RootState) => state.alumnoDiplomas.error;
export const selectAlumnoDiplomasDownloadStatus = (state: RootState) => state.alumnoDiplomas.downloadStatus;
export const selectAlumnoDiplomasDownloadingId = (state: RootState) => state.alumnoDiplomas.downloadingId;

export default alumnoDiplomaSlice.reducer;
